import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import axios from "axios"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import withAuth from "../withAuth"
import { baseUrl } from "../../Network"
import { handleAuthError } from "../../utils"
import { useConfirm } from "../../contexts/ConfirmContext"
import { isMarketerReadOnly } from "../../helpers/adminRole"
import { RULE_ACTIONS, actionLabel, apiErrorMessage } from "./aiTestShared"

const emptyRule = {
  grade: "",
  label: "",
  minPercent: 0,
  maxPercent: 100,
  message: "",
  action: "none",
  targetCurriculumId: "",
  targetLearningPathId: "",
  position: 0,
}

/** Overlaps and uncovered ranges in one set of bands (percent values rounded to 2 decimals). */
const bandProblems = (rules) => {
  const sorted = rules
    .map((r) => ({ ...r, minPercent: Number(r.minPercent), maxPercent: Number(r.maxPercent) }))
    .sort((a, b) => a.minPercent - b.minPercent)
  const out = []
  if (!sorted.length) return out
  if (sorted[0].minPercent > 0) out.push(`Scores from 0% to ${sorted[0].minPercent}% are not covered by any band.`)
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1]
    const cur = sorted[i]
    if (cur.minPercent <= prev.maxPercent) out.push(`Grade ${prev.grade} and grade ${cur.grade} overlap.`)
    else if (cur.minPercent - prev.maxPercent > 0.011) out.push(`Scores between ${prev.maxPercent}% and ${cur.minPercent}% are not covered by any band.`)
  }
  const last = sorted[sorted.length - 1]
  if (last.maxPercent < 100) out.push(`Scores above ${last.maxPercent}% are not covered by any band.`)
  return out
}

const ManageAiTestGradeRules = () => {
  document.title = "Test grading | RYD Admin"
  const { showConfirm } = useConfirm()
  const readOnly = isMarketerReadOnly()
  const [scope, setScope] = useState("global")
  const [scopeTargetId, setScopeTargetId] = useState("")
  const [rules, setRules] = useState([])
  const [defaults, setDefaults] = useState([])
  const [curricula, setCurricula] = useState([])
  const [tests, setTests] = useState([])
  const [paths, setPaths] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyRule)
  const [saving, setSaving] = useState(false)
  const [copying, setCopying] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  useEffect(() => {
    axios
      .get(`${baseUrl}/admin/curriculum/all`)
      .then((res) => setCurricula(Array.isArray(res?.data?.data) ? res.data.data : []))
      .catch(handleAuthError)
    axios
      .get(`${baseUrl}/admin/ai-test/all`)
      .then((res) => setTests(Array.isArray(res?.data?.data) ? res.data.data : []))
      .catch(handleAuthError)
    axios
      .get(`${baseUrl}/admin/learning-path/all`)
      .then((res) => setPaths(Array.isArray(res?.data?.data) ? res.data.data : []))
      .catch(handleAuthError)
  }, [])

  const needsTarget = scope !== "global"

  const load = useCallback(async () => {
    if (needsTarget && !scopeTargetId) {
      setRules([])
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      const params = new URLSearchParams({ scope })
      if (scope === "course") params.set("curriculumId", scopeTargetId)
      if (scope === "test") params.set("testId", scopeTargetId)
      const res = await axios.get(`${baseUrl}/admin/ai-test/grade-rules?${params.toString()}`)
      if (res?.data?.status) {
        setRules(Array.isArray(res.data.data?.rules) ? res.data.data.rules : [])
        setDefaults(Array.isArray(res.data.data?.defaults) ? res.data.data.defaults : [])
      }
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not load grade rules"))
    } finally {
      setLoading(false)
    }
  }, [scope, scopeTargetId, needsTarget])

  useEffect(() => {
    load()
  }, [load])

  const problems = useMemo(() => bandProblems(rules), [rules])
  const courseTitle = (cid) => curricula.find((c) => Number(c.id) === Number(cid))?.title || `Course #${cid}`
  const pathTitle = (pid) => paths.find((p) => Number(p.id) === Number(pid))?.title || `Path #${pid}`

  const targetText = (r) => {
    if (r.action === "next_course" && r.targetCurriculumId) return courseTitle(r.targetCurriculumId)
    if (r.action === "learning_path" && r.targetLearningPathId) return pathTitle(r.targetLearningPathId)
    if (r.action === "next_level") return "Same language, next level"
    return "—"
  }

  const openCreate = () => {
    setEditingId(null)
    setForm({ ...emptyRule })
    setModal(true)
  }

  const openEdit = (r) => {
    setEditingId(r.id)
    setForm({
      grade: r.grade || "",
      label: r.label || "",
      minPercent: r.minPercent,
      maxPercent: r.maxPercent,
      message: r.message || "",
      action: r.action || "none",
      targetCurriculumId: r.targetCurriculumId || "",
      targetLearningPathId: r.targetLearningPathId || "",
      position: r.position || 0,
    })
    setModal(true)
  }

  const body = (f) => ({
    scope,
    ...(scope === "course" ? { curriculumId: Number(scopeTargetId) } : {}),
    ...(scope === "test" ? { testId: Number(scopeTargetId) } : {}),
    grade: String(f.grade).trim(),
    label: String(f.label || "").trim(),
    minPercent: Number(f.minPercent),
    maxPercent: Number(f.maxPercent),
    message: String(f.message || "").trim(),
    action: f.action,
    targetCurriculumId: f.action === "next_course" && f.targetCurriculumId ? Number(f.targetCurriculumId) : null,
    targetLearningPathId: f.action === "learning_path" && f.targetLearningPathId ? Number(f.targetLearningPathId) : null,
    position: Number(f.position) || 0,
  })

  const submit = async () => {
    if (!String(form.grade).trim()) {
      toast.error("Enter a grade, e.g. A")
      return
    }
    try {
      setSaving(true)
      const res = editingId
        ? await axios.put(`${baseUrl}/admin/ai-test/grade-rule/${editingId}`, body(form))
        : await axios.post(`${baseUrl}/admin/ai-test/grade-rule`, body(form))
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Could not save the band")
        return
      }
      toast.success(editingId ? "Band updated" : "Band added")
      setModal(false)
      await load()
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not save the band"))
    } finally {
      setSaving(false)
    }
  }

  const remove = async (r) => {
    const ok = await showConfirm({ title: "Delete band", message: `Delete grade ${r.grade} (${r.minPercent}–${r.maxPercent}%)?`, variant: "danger" })
    if (!ok) return
    try {
      setDeletingId(r.id)
      await axios.delete(`${baseUrl}/admin/ai-test/grade-rule/${r.id}`)
      toast.success("Band deleted")
      await load()
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not delete the band"))
    } finally {
      setDeletingId(null)
    }
  }

  /** Start a course or test from the global bands (or the built-in ones) so admins only tweak. */
  const copyFromGlobal = async () => {
    if (copying) return
    try {
      setCopying(true)
      const res = await axios.get(`${baseUrl}/admin/ai-test/grade-rules?scope=global`)
      const source = res?.data?.data?.rules?.length ? res.data.data.rules : res?.data?.data?.defaults || []
      for (const r of source) {
        await axios.post(`${baseUrl}/admin/ai-test/grade-rule`, body({ ...emptyRule, ...r, targetCurriculumId: r.targetCurriculumId || "", targetLearningPathId: r.targetLearningPathId || "" }))
      }
      toast.success(`Copied ${source.length} band(s)`)
      await load()
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not copy the bands"))
      await load()
    } finally {
      setCopying(false)
    }
  }

  const scopeName = scope === "global" ? "all tests" : scope === "course" ? "this course" : "this test"

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Interactive tests" breadcrumbItem="Test grading" />
          <div className="d-flex flex-wrap gap-2 justify-content-end align-items-center mb-3">
            <Button tag={Link} to="/ai/tests" color="light" size="sm">
              All tests
            </Button>
            {!readOnly && (!needsTarget || scopeTargetId) && (
              <Button color="primary" size="sm" onClick={openCreate}>
                Add band
              </Button>
            )}
          </div>

          <Card>
            <CardBody>
              <p className="text-muted small mb-3">
                A score is turned into a grade and a recommendation using these bands. The most specific set wins: a test&apos;s own
                bands, then its course&apos;s bands, then the global bands, then the built-in defaults (A 85%+, B 70%+, C 50%+, D below).
              </p>
              <Row className="g-2 mb-3">
                <Col md={3}>
                  <Label className="small mb-1">Bands for</Label>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={scope}
                    onChange={(e) => {
                      setScope(e.target.value)
                      setScopeTargetId("")
                    }}
                  >
                    <option value="global">All tests (global)</option>
                    <option value="course">One course</option>
                    <option value="test">One test</option>
                  </Input>
                </Col>
                {scope === "course" && (
                  <Col md={5}>
                    <Label className="small mb-1">Course</Label>
                    <Input type="select" bsSize="sm" value={scopeTargetId} onChange={(e) => setScopeTargetId(e.target.value)}>
                      <option value="">Pick a course</option>
                      {curricula.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.title}
                        </option>
                      ))}
                    </Input>
                  </Col>
                )}
                {scope === "test" && (
                  <Col md={5}>
                    <Label className="small mb-1">Test</Label>
                    <Input type="select" bsSize="sm" value={scopeTargetId} onChange={(e) => setScopeTargetId(e.target.value)}>
                      <option value="">Pick a test</option>
                      {tests.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.title}
                        </option>
                      ))}
                    </Input>
                  </Col>
                )}
              </Row>

              {needsTarget && !scopeTargetId ? (
                <div className="text-muted py-4 text-center">Pick a {scope === "course" ? "course" : "test"} to see its bands.</div>
              ) : loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : (
                <>
                  {problems.length > 0 && (
                    <Alert color="warning" className="small">
                      {problems.map((p) => (
                        <div key={p}>{p}</div>
                      ))}
                      <div className="mt-1">A score in a gap gets the highest band it reached.</div>
                    </Alert>
                  )}
                  {!rules.length && (
                    <Alert color="info" className="small d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <span>
                        No bands for {scopeName} yet, so{" "}
                        {scope === "global" ? "the built-in defaults are used." : "the next set up the chain is used."}
                      </span>
                      {!readOnly && needsTarget && (
                        <Button size="sm" color="info" outline onClick={copyFromGlobal} disabled={copying}>
                          {copying ? (
                            <>
                              <Spinner size="sm" className="me-1" /> Copying…
                            </>
                          ) : (
                            "Copy the global bands here"
                          )}
                        </Button>
                      )}
                    </Alert>
                  )}
                  <div className="table-responsive">
                    <Table className="align-middle table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Grade</th>
                          <th>Label</th>
                          <th>Score range</th>
                          <th>Recommendation</th>
                          <th>Target</th>
                          <th>Message to the learner</th>
                          {!readOnly && <th />}
                        </tr>
                      </thead>
                      <tbody>
                        {(rules.length ? rules : scope === "global" ? defaults.map((d, i) => ({ ...d, id: `default-${i}`, builtIn: true })) : []).map((r) => (
                          <tr key={r.id} className={r.builtIn ? "text-muted" : ""}>
                            <td className="fw-bold">{r.grade}</td>
                            <td>{r.label || "—"}</td>
                            <td className="text-nowrap">
                              {r.minPercent}% – {r.maxPercent}%
                            </td>
                            <td className="small">{actionLabel(r.action)}</td>
                            <td className="small">{targetText(r)}</td>
                            <td className="small" style={{ maxWidth: 320 }}>
                              {r.message || "—"}
                            </td>
                            {!readOnly && (
                              <td className="text-end text-nowrap">
                                {r.builtIn ? (
                                  <span className="small">Built-in</span>
                                ) : (
                                  <>
                                    <Button size="sm" color="secondary" outline className="me-1" onClick={() => openEdit(r)}>
                                      Edit
                                    </Button>
                                    <Button size="sm" color="danger" outline disabled={deletingId === r.id} onClick={() => remove(r)}>
                                      {deletingId === r.id ? <Spinner size="sm" /> : "Delete"}
                                    </Button>
                                  </>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {!readOnly && (
        <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
          <ModalHeader toggle={() => setModal(false)}>{editingId ? "Edit band" : "Add band"}</ModalHeader>
          <ModalBody>
            <Row>
              <Col md={3}>
                <FormGroup>
                  <Label>Grade</Label>
                  <Input value={form.grade} maxLength={10} onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))} placeholder="A" />
                </FormGroup>
              </Col>
              <Col md={5}>
                <FormGroup>
                  <Label>Label</Label>
                  <Input value={form.label} maxLength={50} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Excellent" />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label>From %</Label>
                  <Input type="number" min={0} max={100} step="0.01" value={form.minPercent} onChange={(e) => setForm((f) => ({ ...f, minPercent: e.target.value }))} />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label>To %</Label>
                  <Input type="number" min={0} max={100} step="0.01" value={form.maxPercent} onChange={(e) => setForm((f) => ({ ...f, maxPercent: e.target.value }))} />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Recommendation</Label>
              <Input type="select" value={form.action} onChange={(e) => setForm((f) => ({ ...f, action: e.target.value }))}>
                {RULE_ACTIONS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </Input>
              {form.action === "next_level" && (
                <small className="text-muted">Suggests a visible course in the same language one level up (Beginner → Intermediate → Advanced).</small>
              )}
              {form.action === "review_lessons" && (
                <small className="text-muted">Lists the lessons tagged on the cards the learner missed.</small>
              )}
            </FormGroup>
            {form.action === "next_course" && (
              <FormGroup>
                <Label>Course to recommend</Label>
                <Input type="select" value={form.targetCurriculumId} onChange={(e) => setForm((f) => ({ ...f, targetCurriculumId: e.target.value }))}>
                  <option value="">Pick a course</option>
                  {curricula.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                      {c.isVisible ? "" : " — hidden"}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            )}
            {form.action === "learning_path" && (
              <FormGroup>
                <Label>Learning path to recommend</Label>
                <Input type="select" value={form.targetLearningPathId} onChange={(e) => setForm((f) => ({ ...f, targetLearningPathId: e.target.value }))}>
                  <option value="">Pick a learning path</option>
                  {paths.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                      {p.isVisible ? "" : " — hidden"}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            )}
            <FormGroup>
              <Label>Message to the learner</Label>
              <Input
                type="textarea"
                rows={3}
                maxLength={2000}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                placeholder="Brilliant work! You're ready for the next challenge."
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setModal(false)}>
              Cancel
            </Button>
            <Button color="primary" onClick={submit} disabled={saving}>
              {saving ? <Spinner size="sm" /> : "Save band"}
            </Button>
          </ModalFooter>
        </Modal>
      )}
      <ToastContainer />
    </React.Fragment>
  )
}

export default withAuth(ManageAiTestGradeRules)
